/**
 * Installed plugins (ADR 0025): each one is a page in the hidden "Plugins"
 * folder, its manifest and code in the property `nexus.plugin`. So plugins sync
 * like pages, and removing one goes through the trash.
 */
import { ensureSpecialPage } from '@nexus/workspace';
import type { PageRecord, Store } from '@nexus/store';
import { readManifest, type PluginManifest } from './manifest';

export const PLUGIN_PROP = 'nexus.plugin';
export const PLUGINS_FOLDER = 'Plugins';
const MAX_CODE = 200_000;

export interface StoredPlugin {
  manifest: PluginManifest;
  code: string;
  enabled: boolean;
  source: 'ai' | 'manual' | 'import';
  /** For AI-built plugins: what was asked, newest last (to keep refining it). */
  requests: string[];
  updatedAt: number;
}

export interface InstalledPlugin extends StoredPlugin {
  pageId: string;
}

export const pluginsFolder = (store: Store) =>
  ensureSpecialPage(store, 'plugins_parent_id', PLUGINS_FOLDER, '🧩');

function readStored(raw: unknown): StoredPlugin | null {
  const v = (raw && typeof raw === 'object' ? raw : null) as Record<string, unknown> | null;
  if (!v || typeof v.code !== 'string') return null;
  const m = readManifest(v.manifest);
  if ('error' in m) return null;
  return {
    manifest: m.manifest,
    code: v.code,
    enabled: v.enabled === true,
    source: v.source === 'ai' || v.source === 'import' ? v.source : 'manual',
    requests: Array.isArray(v.requests)
      ? v.requests.filter((r): r is string => typeof r === 'string')
      : [],
    updatedAt: typeof v.updatedAt === 'number' ? v.updatedAt : 0,
  };
}

export async function listPlugins(store: Store): Promise<InstalledPlugin[]> {
  const folderId = await store.getSetting('plugins_parent_id');
  if (!folderId) return [];
  const out: InstalledPlugin[] = [];
  for (const p of await store.listPagesWithProps({ parentId: folderId })) {
    const stored = readStored(p.props[PLUGIN_PROP]);
    if (stored) out.push({ ...stored, pageId: p.id });
  }
  return out.sort((a, b) => a.manifest.name.localeCompare(b.manifest.name));
}

/**
 * Install or update a plugin (same manifest id = same plugin). A new or changed
 * plugin starts switched off when its permissions grew, so the user sees them.
 */
export async function savePlugin(
  store: Store,
  input: {
    manifest: PluginManifest;
    code: string;
    source: StoredPlugin['source'];
    request?: string;
    enabled?: boolean;
  },
): Promise<InstalledPlugin> {
  if (input.code.length > MAX_CODE) throw new Error('De code is te groot (max. 200 kB).');
  const existing = (await listPlugins(store)).find((p) => p.manifest.id === input.manifest.id);
  let page: PageRecord;
  if (existing) {
    page = (await store.getPage(existing.pageId))!;
    if (page.title !== input.manifest.name)
      await store.updatePage(page.id, { title: input.manifest.name });
  } else {
    page = await store.createPage({
      title: input.manifest.name,
      parentId: (await pluginsFolder(store)).id,
    });
  }
  const stored: StoredPlugin = {
    manifest: input.manifest,
    code: input.code,
    enabled:
      input.enabled ??
      (existing ? existing.enabled && !widened(existing.manifest, input.manifest) : false),
    source: input.source,
    requests: [...(existing?.requests ?? []), ...(input.request ? [input.request] : [])].slice(-20),
    updatedAt: Date.now(),
  };
  await store.setProp(page.id, PLUGIN_PROP, stored);
  return { ...stored, pageId: page.id };
}

/** Did the new version ask for more than the old one had? */
export function widened(before: PluginManifest, after: PluginManifest): boolean {
  return (
    (after.permissions.read && !before.permissions.read) ||
    (after.permissions.write && !before.permissions.write) ||
    after.permissions.network.some((d) => !before.permissions.network.includes(d))
  );
}

export async function setPluginEnabled(store: Store, pageId: string, enabled: boolean) {
  const stored = readStored((await store.getProps(pageId))[PLUGIN_PROP]);
  if (stored) await store.setProp(pageId, PLUGIN_PROP, { ...stored, enabled });
}

/** A plugin as a file to share or keep (`.nexus-plugin.json`). */
export function exportPlugin(p: StoredPlugin): string {
  return JSON.stringify(
    { format: 'nexus-plugin', version: 1, manifest: p.manifest, code: p.code },
    null,
    2,
  );
}

export function parsePluginFile(
  text: string,
): { manifest: PluginManifest; code: string } | { error: string } {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return { error: 'Geen geldig pluginbestand (JSON).' };
  }
  const v = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  if (v.format !== 'nexus-plugin' || typeof v.code !== 'string')
    return { error: 'Geen Nexus-pluginbestand.' };
  const m = readManifest(v.manifest);
  return 'error' in m ? m : { manifest: m.manifest, code: v.code };
}
